import { useState, useCallback, useEffect } from 'react';
import {
  Edit3, MapPin, Clock, DollarSign, Image,
  Utensils, Save, Trash2, Archive,
  ArrowLeft, MoreVertical, TagIcon,
  CalendarRange, CheckCircle2, AlertCircle,
  ExternalLink, ChevronRight, X, Loader2
} from 'lucide-react';
import type { Restaurant, RestaurantFormData, FormGallery, Gallery, FormGalleryItem, FormFoodItem } from '../restaurant.types';
import { approveRestaurant, archiveRestaurant, deleteRestaurant, fetchSecureRestaurantById, updateRestaurant } from '../../../lib/api/restaurant.apis';
import { useNavigate, useParams } from 'react-router-dom';
import BasicInfo from './EditComponents/BasicInfo';
import Location from './EditComponents/Location';
import CuisineAndTags from './EditComponents/CuisineTags';
import OpeningHours from './EditComponents/OpeningHours';
import Budget from './EditComponents/Budget';
import RenderGallery from './EditComponents/Gallery';
import { StaffManagement } from './EditComponents/StaffManagement';
import { useAuth } from '../../../Context/AuthContext';
import { slugify } from '../../Public/Explore/services';

interface HoveredSection { id: string; x: number; y: number; label: string; }

const restaurantToFormData = (restaurant: Restaurant): RestaurantFormData => ({
  id: restaurant._id,
  name: restaurant.name,
  description: restaurant.description,
  location: restaurant.location,
  cuisine: restaurant.cuisine,
  typeOfFood: restaurant.typeOfFood,
  experience: restaurant.experience,
  tags: restaurant.tags,
  budgetLevel: restaurant.budgetLevel,
  specialFeatures: restaurant.specialFeatures,
  contactInfo: restaurant.contactInfo,
  priceRange: restaurant.priceRange || { min: 1500, max: 5000 },
  openingHours: restaurant.openingHours,
  gallery: restaurant.gallery || { cover: null, food: [], ambiance: [], menu: null }
});

// ─── TOAST NOTIFICATION ──────────────────────────────────────────────────────
type ToastType = 'success' | 'error' | 'info';
interface Toast { id: string; msg: string; type: ToastType; }

// ─── CONFIRM MODAL ───────────────────────────────────────────────────────────
interface ConfirmModalProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  confirmLabel: string;
  confirmClass: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  icon, iconBg, title, description, confirmLabel, confirmClass, loading, onConfirm, onCancel,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fadeIn">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-0 overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center ${iconBg}`}>
            {icon}
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 flex items-center justify-end gap-3 bg-gray-50/60">
        <button onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          className={`px-5 py-2 text-sm font-bold text-white rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 ${confirmClass}`}>
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? 'Processing…' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const RestaurantEdit = () => {
  const [formData, setFormData] = useState<RestaurantFormData>(null);
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [restaurant, setRestaurant] = useState<Restaurant>(null);
  const [activeSection, setActiveSection] = useState('basic');
  const [hoveredSection, setHoveredSection] = useState<HoveredSection | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [fetchingRest, setFetchingRest] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── TOAST HELPERS ──────────────────────────────────────────────────────────
  const addToast = (msg: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  };

  // ── FETCH ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingRest(true);
        const res = await fetchSecureRestaurantById(restaurantId);
        if (typeof res === 'string') { setFetchingRest(false); return navigate('/dashboard'); }
        setRestaurant(res);
        setFormData(restaurantToFormData(res));
      } catch (e) {
        navigate('/dashboard');
      } finally {
        setFetchingRest(false);
      }
    };
    fetchData();
  }, [restaurantId]);

  // ── SECTIONS ──────────────────────────────────────────────────────────────
  const sections = [
    { id: 'basic',            label: 'Basic Info',        sub: 'Name, description',       icon: Edit3,         color: 'text-orange-500', bg: 'bg-orange-50'  },
    { id: 'location',         label: 'Location',          sub: 'Address & map',           icon: MapPin,        color: 'text-blue-500',   bg: 'bg-blue-50'    },
    { id: 'cuisine',          label: 'Cuisine & Tags',    sub: 'Food type, categories',   icon: Utensils,      color: 'text-emerald-500',bg: 'bg-emerald-50' },
    { id: 'hours',            label: 'Opening Hours',     sub: 'Schedule & timings',      icon: Clock,         color: 'text-violet-500', bg: 'bg-violet-50'  },
    { id: 'budget',           label: 'Price & Budget',    sub: 'Range & level',           icon: DollarSign,    color: 'text-amber-500',  bg: 'bg-amber-50'   },
    { id: 'gallery',          label: 'Photos & Gallery',  sub: 'Cover, food, ambiance',   icon: Image,         color: 'text-pink-500',   bg: 'bg-pink-50'    },
    { id: 'staff-management', label: 'Staff',             sub: 'Team & roles',            icon: TagIcon,       color: 'text-teal-500',   bg: 'bg-teal-50'    },
    { id: 'reservations',     label: 'Reservations',      sub: 'Bookings & schedule',     icon: CalendarRange, color: 'text-indigo-500', bg: 'bg-indigo-50', isNav: true },
  ];

  // ── FORM UPDATE ───────────────────────────────────────────────────────────
  const updateFormData = useCallback((newData: Partial<RestaurantFormData>) => {
    setFormData(prev => {
      if (!prev) return prev;
      return {
        ...prev, ...newData,
        gallery: newData.gallery ? {
          ...prev.gallery, ...newData.gallery,
          food:     newData.gallery.food     ? [...newData.gallery.food]     : prev.gallery.food,
          menu:     newData.gallery.menu     ? [...newData.gallery.menu]     : prev.gallery.menu,
          ambiance: newData.gallery.ambiance ? [...newData.gallery.ambiance] : prev.gallery.ambiance,
          cover:    newData.gallery.cover    ?? prev.gallery.cover,
        } : prev.gallery,
        priceRange:   newData.priceRange   ? { ...prev.priceRange,   ...newData.priceRange   } : prev.priceRange,
        location:     newData.location     ? { ...prev.location,     ...newData.location     } : prev.location,
        contactInfo:  newData.contactInfo  ? { ...prev.contactInfo,  ...newData.contactInfo  } : prev.contactInfo,
        openingHours: newData.openingHours ? { ...prev.openingHours, ...newData.openingHours } : prev.openingHours,
      };
    });
  }, []);

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    const convertGallery = (fg: FormGallery): Gallery => {
      const norm = (items?: (FormGalleryItem | FormFoodItem)[]) =>
        (items || []).filter(i => i.url).map(i => ({
          url: typeof i.url === 'string' ? i.url : '',
          publicId: i.publicId, id: i.id, label: i.label,
        }));
      return {
        cover: fg.cover?.url ? { url: typeof fg.cover.url === 'string' ? fg.cover.url : '', publicId: fg.cover.publicId, id: fg.cover.id, label: fg.cover.label } : null,
        food: norm(fg.food), ambiance: norm(fg.ambiance), menu: norm(fg.menu),
      };
    };
    try {
      const gallery = convertGallery(formData.gallery);
      const updated: Restaurant = {
        ...restaurant, ...formData, gallery, updatedAt: new Date(),
      };
      await updateRestaurant(updated._id!, updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      addToast('Restaurant saved successfully!', 'success');
    } catch {
      addToast('Failed to save. Please check uploads and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── ACTIONS ───────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    setDeleting(true);
    try { await deleteRestaurant(id); navigate('/dashboard'); }
    catch { addToast('Failed to delete restaurant.', 'error'); }
    finally { setDeleting(false); }
  };

  const handleApprove = async (id: string) => {
    setApproving(true);
    try { await approveRestaurant(id); navigate('/dashboard'); }
    catch { addToast('Failed to submit for approval.', 'error'); }
    finally { setApproving(false); }
  };

  const handleArchive = async (id: string) => {
    setLoading(true);
    try { await archiveRestaurant(id); addToast('Restaurant archived.', 'info'); setShowArchiveConfirm(false); }
    catch { addToast('Failed to archive restaurant.', 'error'); }
    finally { setLoading(false); }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'basic':            return <BasicInfo formData={formData} updateFormData={updateFormData} />;
      case 'location':         return <Location formData={formData} updateFormData={updateFormData} />;
      case 'cuisine':          return <CuisineAndTags formData={formData} updateFormData={updateFormData} />;
      case 'hours':            return <OpeningHours formData={formData} updateFormData={updateFormData} />;
      case 'budget':           return <Budget formData={formData} updateFormData={updateFormData} />;
      case 'gallery':          return <RenderGallery restaurantId={restaurantId} restaurantName={restaurant.name} formData={formData} publishedPdfUrl={restaurant.menuPdfUrl} updateFormData={updateFormData} setFormData={setFormData} />;
      case 'staff-management': return <StaffManagement restaurant={restaurant} />;
    }
  };

  if (fetchingRest) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-[#FFD166] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm font-medium text-gray-500">Loading restaurant…</p>
      </div>
    </div>
  );

  if (!restaurant) return null;

  const handleSectionClick = (section: typeof sections[0]) => {
    if (section.isNav) {
      window.open(`/reservation/${restaurantId}/${slugify(restaurant?.name)}`, '_blank');
    } else {
      setActiveSection(section.id);
    }
  };

  const isOwner = restaurant?.ownerId === user?.id;
  const activeSec = sections.find(s => s.id === activeSection);

  return (
    <div className="min-h-screen bg-[#F7F6F3]">

      {/* ═══ TOAST STACK ═══════════════════════════════════════════════════ */}
      <div className="fixed top-4 right-4 z-[10] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold border backdrop-blur-sm animate-slideIn
            ${t.type === 'success' ? 'bg-white border-green-100 text-green-700' :
              t.type === 'error'   ? 'bg-white border-red-100 text-red-600'     :
              'bg-white border-blue-100 text-blue-600'}`}>
            {t.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> :
             t.type === 'error'   ? <AlertCircle  className="w-4 h-4 text-red-500 flex-shrink-0"   /> :
             <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
            {t.msg}
          </div>
        ))}
      </div>

      {/* ═══ DESKTOP HEADER ════════════════════════════════════════════════ */}
      <header className="hidden lg:block sticky top-17 z-21 bg-white/90 backdrop-blur-md border-b border-gray-200/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Left */}
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')}
                className="group flex items-center justify-center w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors">
                <ArrowLeft className="w-4 h-4 text-gray-600 group-hover:-translate-x-0.5 transition-transform" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#FFF8E7] border border-[#FFD166]/40 flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-4 h-4 text-[#D4A017]" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 leading-tight">{restaurant.name}</h1>
                  <p className="text-xs text-gray-400 font-medium">
                    {activeSec?.label && <span className="text-[#D4A017]">{activeSec.label}</span>}
                  </p>
                </div>
              </div>

              {/* Status badge */}
              <div className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border
                ${restaurant.isApproved
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${restaurant.isApproved ? 'bg-green-500' : 'bg-amber-400'}`} />
                {restaurant.isApproved ? 'Published' : 'Draft'}
              </div>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {isOwner && (
                <>
                  {restaurant.isApproved ? (
                    <button onClick={() => setShowArchiveConfirm(true)}
                      className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  ) : (
                    <button onClick={() => handleApprove(restaurant._id)}
                      className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {approving ? 'Submitting…' : 'Submit for Approval'}
                    </button>
                  )}
                  <button onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </>
              )}

              {/* Navigate to Reservations */}
              <button onClick={() => window.open(`/reservation/${restaurantId}/${slugify(restaurant?.name)}`, '_blank')}
                className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 transition-colors">
                <CalendarRange className="w-3.5 h-3.5" />
                Reservations
                <ExternalLink className="w-3 h-3 opacity-60" />
              </button>

              <div className="w-px h-5 bg-gray-200" />

              <button onClick={() => navigate('/dashboard')}
                className="px-3.5 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>

              <button onClick={handleSave} disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-xl transition-all shadow-sm disabled:opacity-60
                  ${saveSuccess
                    ? 'bg-green-500 text-white border-transparent'
                    : 'bg-[#FFD166] text-[#111827] border border-[#E8C14A] hover:bg-[#E8C14A]'}`}>
                {saving       ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</> :
                 saveSuccess  ? <><CheckCircle2 className="w-3.5 h-3.5" />Saved!</>          :
                                <><Save className="w-3.5 h-3.5" />Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ MOBILE HEADER ══════════════════════════════════════════════════ */}
      <div className="lg:hidden sticky top-17 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3">

          {/* Top row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')}
                className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">{restaurant.name}</h1>
                <p className="text-[11px] text-gray-400">Restaurant Settings</p>
              </div>
            </div>

            {/* 3-dot menu */}
            <div className="relative">
              <button onClick={() => setShowActionMenu(!showActionMenu)}
                className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </button>

              {showActionMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowActionMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="p-1">
                      {/* Reservations */}
                      <button onClick={() => { window.open(`/reservation/${restaurantId}/${slugify(restaurant?.name)}`, '_blank'); setShowActionMenu(false); }}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors text-sm font-semibold">
                        <CalendarRange className="w-4 h-4" />
                        View Reservations
                        <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
                      </button>

                      <div className="my-1 border-t border-gray-100" />

                      {isOwner && (
                        <>
                          {!restaurant.isApproved ? (
                            <button onClick={() => { handleApprove(restaurant._id); setShowActionMenu(false); }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors text-sm font-medium">
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              {approving ? 'Submitting…' : 'Submit for Approval'}
                            </button>
                          ) : (
                            <button onClick={() => { setShowArchiveConfirm(true); setShowActionMenu(false); }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-amber-600 hover:bg-amber-50 rounded-xl transition-colors text-sm font-medium">
                              <Archive className="w-4 h-4" />
                              Archive Restaurant
                            </button>
                          )}
                          <button onClick={() => { setShowDeleteConfirm(true); setShowActionMenu(false); }}
                            className="flex items-center gap-3 w-full px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium">
                            <Trash2 className="w-4 h-4" />
                            Delete Restaurant
                          </button>
                          <div className="my-1 border-t border-gray-100" />
                        </>
                      )}

                      <button onClick={() => { handleSave(); setShowActionMenu(false); }} disabled={saving}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-[#111827] hover:bg-[#FFF8E7] rounded-xl transition-colors text-sm font-bold disabled:opacity-50">
                        <Save className="w-4 h-4 text-[#D4A017]" />
                        {saving ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section tabs — icons only with active indicator */}
          <div className="flex items-center justify-between gap-1 overflow-x-auto hide-scrollbar pb-0.5">
            {sections.filter(sec => sec.id !== "reservations").map(sec => {
              const Icon = sec.icon;
              const isActive = activeSection === sec.id && !sec.isNav;
              return (
                <div key={sec.id} className="relative flex-shrink-0"
                  onMouseEnter={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setHoveredSection({ id: sec.id, x: r.left + r.width / 2, y: r.top, label: sec.label });
                  }}
                  onMouseLeave={() => setHoveredSection(null)}>
                  <button onClick={() => handleSectionClick(sec)}
                    className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all
                      ${isActive
                        ? `${sec.bg} ${sec.color} shadow-sm ring-1 ring-inset ring-current/20`
                        : sec.isNav
                          ? 'bg-indigo-50 text-indigo-500'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}>
                    <Icon className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tooltip */}
        {hoveredSection && (
          <div className="fixed z-50 pointer-events-none"
            style={{ left: hoveredSection.x, top: hoveredSection.y - 38, transform: 'translateX(-50%)' }}>
            <div className="bg-gray-900 text-white text-[11px] font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap shadow-lg">
              {hoveredSection.label}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </div>
          </div>
        )}
      </div>

      {/* ═══ MAIN CONTENT ═══════════════════════════════════════════════════ */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-1 lg:py-2">
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-3">

          {/* ── DESKTOP SIDEBAR ────────────────────────────────────────── */}
          <aside className="hidden lg:flex flex-col gap-3 w-72 flex-shrink-0">

            {/* Nav card */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
              <div className="px-4 pt-4 pb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Settings</p>
              </div>
              <nav className="px-2 pb-3 flex flex-col gap-0.5">
                {sections.filter(s => !s.isNav).map(sec => {
                  const Icon = sec.icon;
                  const isActive = activeSection === sec.id;
                  return (
                    <button key={sec.id} onClick={() => setActiveSection(sec.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group text-left
                        ${isActive ? `${sec.bg} ${sec.color}` : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                        ${isActive ? 'bg-white/60' : 'bg-gray-100 group-hover:bg-white'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold leading-tight ${isActive ? '' : 'text-gray-700'}`}>{sec.label}</p>
                        <p className={`text-[11px] truncate ${isActive ? 'opacity-70' : 'text-gray-400'}`}>{sec.sub}</p>
                      </div>
                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform opacity-0 group-hover:opacity-100
                        ${isActive ? 'opacity-100 rotate-90' : ''}`} />
                    </button>
                  );
                })}
              </nav>

              {/* Reservations nav separator */}
              <div className="mx-4 border-t border-gray-100 mb-2" />
              <div className="px-2 pb-3">
                <button onClick={() => window.open(`/reservation/${restaurantId}/${slugify(restaurant?.name)}`, '_blank')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all group text-left border border-indigo-100">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <CalendarRange className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-indigo-700 leading-tight">Reservations</p>
                    <p className="text-[11px] text-indigo-400">Bookings & schedule</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-indigo-400 opacity-70 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            </div>

            {/* Quick info card */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Restaurant</p>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFF8E7] flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-5 h-5 text-[#D4A017]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{restaurant.name}</p>
                  <p className="text-xs text-gray-400 truncate">{restaurant.location?.city || 'Location not set'}</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold
                ${restaurant.isApproved ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                <div className={`w-2 h-2 rounded-full ${restaurant.isApproved ? 'bg-green-500' : 'bg-amber-400'} animate-pulse`} />
                {restaurant.isApproved ? 'Published & Live' : 'Draft — Not Published'}
              </div>
            </div>
          </aside>

          {/* ── CONTENT AREA ───────────────────────────────────────────── */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            {/* Content card */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm">
              <div className="p-4 sm:p-6">
                {renderSection()}
              </div>
            </div>

            {/* Bottom save bar — visible on scroll for long forms */}
            <div className="sticky bottom-4 lg:hidden">
              <div className="mx-auto max-w-sm">
                <button onClick={handleSave} disabled={saving}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-bold rounded-2xl shadow-lg transition-all disabled:opacity-60
                    ${saveSuccess ? 'bg-green-500 text-white' : 'bg-[#FFD166] text-[#111827]'}`}>
                  {saving      ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>          :
                   saveSuccess ? <><CheckCircle2 className="w-4 h-4" />Saved!</>                   :
                                 <><Save className="w-4 h-4" />Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ═══ CONFIRM MODALS ════════════════════════════════════════════════ */}
      {showDeleteConfirm && (
        <ConfirmModal
          icon={<Trash2 className="w-5 h-5 text-red-600" />}
          iconBg="bg-red-100"
          title="Delete Restaurant"
          description={`Are you sure you want to permanently delete "${restaurant.name}"? This cannot be undone and all data will be lost.`}
          confirmLabel="Delete Forever"
          confirmClass="bg-red-600 hover:bg-red-700"
          loading={deleting}
          onConfirm={() => handleDelete(restaurant._id)}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmModal
          icon={<Archive className="w-5 h-5 text-amber-600" />}
          iconBg="bg-amber-100"
          title="Archive Restaurant"
          description={`Are you sure you want to archive "${restaurant.name}"? It will be hidden from public listings but can be restored later.`}
          confirmLabel="Archive Restaurant"
          confirmClass="bg-amber-500 hover:bg-amber-600"
          loading={loading}
          onConfirm={() => handleArchive(restaurant._id)}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideIn  { from { opacity:0; transform: translateX(16px); } to { opacity:1; transform: translateX(0); } }
        @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
        .animate-slideIn    { animation: slideIn 0.25s ease; }
        .animate-fadeIn     { animation: fadeIn  0.2s ease; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default RestaurantEdit;